import { paths, routeKeys, type RouteKey } from 'url-registry';
import { transcriptWrapperChunk } from 'components/transcript/transcriptChunks';
import { startTranscriptAccessRequest } from 'hooks/transcriptAccessBoot';
import {
  campaignDetailsChunk,
  jobEditorChunk,
} from 'pages/jobs/shell/jobTabChunks';
import { createChunkLoader } from 'utils/chunkLoader';
import { isEntityUuid } from 'utils/selectionDeepLink';

import type { ChunkLoader, Mount } from './types';

// Every page loader is shared by all its callers, so the router's lazy page
// takes the boot warm-up's import (startRecruiterRouteWarmup) instead of
// starting its own: Vite's preload helper waits for a chunk's stylesheets only
// on the first import of it, and a second import could render the page before
// its CSS has loaded. A failed import is forgotten, so a later call retries.
const shared = <T>(importChunk: () => Promise<T>): (() => Promise<T>) =>
  createChunkLoader(importChunk).load;

const loadJobsPage = shared(() => import('pages/jobs/list/JobsPage'));
const loadJobPage = shared(() => import('pages/jobs/shell/JobPage'));
const loadJobEditor = jobEditorChunk.load;
const loadCampaignDetails = campaignDetailsChunk.load;
const loadTranscriptWrapper = transcriptWrapperChunk.load;
const loadAdminTabs = shared(() => import('pages/admin/AdminTabsPage'));
const loadAnalytics = shared(() => import('pages/analytics/AnalyticsPage'));

/**
 * The transcript drawer's chunk when the URL names a candidate, whose drawer
 * the candidates table opens on load (useCandidateTranscriptDrawer). A
 * malformed id opens the broken-link modal instead, so it warms nothing.
 */
const warmCandidateTranscript = (
  search: URLSearchParams
): readonly ChunkLoader[] =>
  isEntityUuid(search.get('candidateId')) ? [loadTranscriptWrapper] : [];

/**
 * Warm only the tab a saved job opens: Configure for default/unknown tabs and
 * historical versions, or Candidates (including legacy `contacts`) and the
 * transcript drawer of a candidate the URL names. Configure mounts on its
 * first visit, so other tabs do not need its chunk on initial load.
 * The tab values are spelled out rather than imported from useJobTabState,
 * which would pull the job shell's dependencies into the entry.
 */
export const warmJobPageTabs = (
  search: URLSearchParams
): readonly ChunkLoader[] => {
  if (search.get('versionId')) return [loadJobEditor];
  const tab = search.get('tab');
  if (tab === 'candidates' || tab === 'contacts') {
    return [loadCampaignDetails, ...warmCandidateTranscript(search)];
  }
  if (
    tab === 'ai-sourcing' ||
    tab === 'testing' ||
    tab === 'dev-tools' ||
    tab === 'loader' ||
    tab === 'questions'
  ) {
    return [];
  }
  return [loadJobEditor];
};

export const routeMounts: { [K in RouteKey]: Mount } = {
  HOME: { kind: 'page', load: loadJobsPage, guard: 'protected' },
  JOBS: { kind: 'page', load: loadJobsPage, guard: 'protected' },
  EXTERNAL_AGENT: {
    kind: 'page',
    load: shared(() => import('pages/external-agent/ExternalAgentRoute')),
    guard: 'protected',
  },
  SOURCING: {
    kind: 'page',
    load: shared(() => import('pages/sourcing/StandaloneSourcingPage')),
    guard: 'protected',
  },
  INTERVIEWS: {
    kind: 'page',
    load: shared(() => import('pages/notifications/NotificationsPage')),
    guard: 'protected',
    wrapAts: true,
  },
  ANALYTICS: {
    kind: 'page',
    load: loadAnalytics,
    guard: 'protected',
    wrapAts: true,
  },
  QUESTIONS: {
    kind: 'page',
    load: shared(() => import('pages/org-questions/OrgQuestionsPage')),
    guard: 'protected',
  },
  NOTES: {
    kind: 'page',
    load: shared(() => import('pages/notes/dashboard/Notes')),
    guard: 'public',
  },
  SUPPORT_FEEDBACK: {
    kind: 'page',
    load: shared(() => import('pages/feedback/FeedbackPage')),
    guard: 'protected',
  },
  SETTINGS: { kind: 'page', load: loadAdminTabs, guard: 'protected' },
  SETTINGS_TAB: { kind: 'page', load: loadAdminTabs, guard: 'protected' },
  USER_SETTINGS: {
    kind: 'page',
    load: shared(() => import('pages/user-settings/UserSettings')),
    guard: 'protected',
  },
  BILLING: {
    kind: 'page',
    load: shared(() => import('pages/billing/BillingPage')),
    guard: 'protected',
  },
  ALERTS: {
    kind: 'page',
    load: shared(() => import('pages/alerts/AlertsPage')),
    guard: 'protected',
  },
  AUDIT_LOG: {
    kind: 'page',
    load: shared(() => import('pages/admin/AuditEventsPage')),
    guard: 'protected',
  },
  MANAGE_PHONE_NUMBERS: {
    kind: 'page',
    load: shared(
      () => import('pages/twilio-phonenumbers/ManagePhoneNumbersPage')
    ),
    guard: 'protected',
  },
  JOBS_FOLDER: { kind: 'page', load: loadJobsPage, guard: 'protected' },
  JOBS_EDITOR: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
    warm: warmJobPageTabs,
  },
  JOBS_FOLDER_EDITOR: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
    warm: warmJobPageTabs,
  },
  CAMPAIGN_DETAILS: {
    kind: 'page',
    load: loadCampaignDetails,
    guard: 'protected',
    warm: warmCandidateTranscript,
  },
  SUBMITTAL_REPORT: {
    kind: 'page',
    load: shared(() => import('pages/jobs/list/SubmittalReportsPage')),
    guard: 'submittal',
  },
  NOTE: {
    kind: 'page',
    load: shared(() => import('pages/notes/detail/NotePage')),
    guard: 'public',
  },
  ANALYTICS_DASHBOARD: {
    kind: 'page',
    load: loadAnalytics,
    guard: 'protected',
    wrapAts: true,
  },
  LOGIN: {
    kind: 'page',
    load: shared(() => import('pages/login/CustomStytchLoginPage')),
    guard: 'login',
    hideNav: true,
  },
  SUPPORT_MICROSOFT_CALLBACK: {
    kind: 'page',
    load: shared(() => import('pages/login/SupportMicrosoftCallback')),
    guard: 'public',
    hideNav: true,
  },
  AUTHENTICATE: {
    kind: 'page',
    load: shared(() => import('pages/login/RedirectLinkHandler')),
    guard: 'public',
    hideNav: true,
  },
  OAUTH_CONSENT: {
    kind: 'page',
    load: shared(() => import('pages/oauth-consent/OauthConsentPage')),
    guard: 'public',
    hideNav: true,
  },
  OAUTH_REDIRECT: {
    kind: 'page',
    load: shared(() => import('pages/email-settings/OAuthRedirect')),
    guard: 'protected',
  },
  OAUTH_CALENDAR_REDIRECT: {
    kind: 'page',
    load: shared(() => import('pages/email-settings/OAuthCalendarRedirect')),
    guard: 'protected',
  },
  OAUTH_USER_CALENDAR_REDIRECT: {
    kind: 'page',
    load: shared(() => import('pages/user-settings/OAuthUserCalendarRedirect')),
    guard: 'protected',
  },
  OAUTH_NOTIFICATION_REDIRECT: {
    kind: 'page',
    load: shared(() => import('pages/admin/OAuthNotificationRedirect')),
    guard: 'protected',
  },
  OAUTH_ZOOM_REDIRECT: {
    kind: 'page',
    load: shared(() => import('pages/admin/OAuthZoomRedirect')),
    guard: 'protected',
  },
  RESTRICTED_ACCESS: {
    kind: 'page',
    load: shared(() => import('pages/restricted-access/RestrictedAccessPage')),
    guard: 'restricted',
    hideNav: true,
  },
  JOBS_EDITOR_NEW: { kind: 'page', load: loadJobPage, guard: 'protected' },
  JOBS_FOLDER_EDITOR_NEW: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
  },
  JOBS_EDITOR_NEW_PHONE: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
  },
  JOBS_EDITOR_NEW_WEB: { kind: 'page', load: loadJobPage, guard: 'protected' },
  JOBS_FOLDER_EDITOR_NEW_PHONE: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
  },
  JOBS_FOLDER_EDITOR_NEW_WEB: {
    kind: 'page',
    load: loadJobPage,
    guard: 'protected',
  },
  NOTES_LEGACY: { kind: 'redirect', to: 'NOTES', preserveSearch: true },
  BILLING_LEGACY: { kind: 'redirect', to: 'BILLING' },
  NOTES_GOOGLE: {
    kind: 'page',
    load: shared(() => import('pages/notes/google/GoogleMeetingNote')),
    guard: 'public',
  },
  NOTES_TEAMS_HOME: {
    kind: 'page',
    load: shared(() => import('pages/notes/teams/TeamsPersonalHome')),
    guard: 'public',
    hideNav: true,
  },
  NOTES_TEAMS: {
    kind: 'page',
    load: shared(() => import('pages/notes/teams/TeamsMeetingNote')),
    guard: 'public',
  },
  NOTES_ZOOM: {
    kind: 'page',
    load: shared(() => import('pages/notes/zoom/ZoomMeetingNote')),
    guard: 'public',
  },
  NOTES_ZOOM_SELECT_ORG: {
    kind: 'page',
    load: shared(() => import('pages/notes/zoom/ZoomSelectOrg')),
    guard: 'public',
  },
  BULLHORN_JOB_TAB: {
    kind: 'page',
    load: shared(() => import('pages/bullhorn/BullhornJobTabHandler')),
    guard: 'protected',
    wrapAts: true,
  },
  BULLHORN_CANDIDATE_TAB: {
    kind: 'page',
    load: shared(() => import('pages/bullhorn/BullhornCandidateTabHandler')),
    guard: 'protected',
    wrapAts: true,
  },
  EMAIL_DECISION_ACCEPT: {
    kind: 'page',
    load: shared(() => import('pages/email-decision/EmailDecisionAccept')),
    guard: 'protected',
    hideNav: true,
  },
  EMAIL_DECISION_DISPOSITION: {
    kind: 'page',
    load: shared(() => import('pages/email-decision/EmailDecisionDisposition')),
    guard: 'protected',
    hideNav: true,
  },
  APPROVE_REISSUE: {
    kind: 'page',
    hideNav: true,
    guard: 'public',
    load: shared(() =>
      import('pages/interview-reissue/ApproveReissuePage').then((module) => ({
        default: module.ApproveReissuePage,
      }))
    ),
  },
  REJECT_REISSUE: {
    kind: 'page',
    hideNav: true,
    guard: 'public',
    load: shared(() =>
      import('pages/interview-reissue/RejectReissuePage').then((module) => ({
        default: module.RejectReissuePage,
      }))
    ),
  },
  SHARE_TRANSCRIPT: {
    kind: 'page',
    load: shared(
      () => import('pages/jobs/candidates/NoLoginCampaignTranscript')
    ),
    guard: 'share',
    // The page loads the transcript lazily, alongside its access check.
    warm: warmCandidateTranscript,
    // ShareLinkRoute renders the page straight away without a session, and
    // its access check is the page's first request.
    requestWithoutSession: startTranscriptAccessRequest,
  },
  SHARED_SUBMITTAL_REPORT: {
    kind: 'page',
    load: shared(() => import('pages/jobs/list/SharedSubmittalReportPage')),
    guard: 'submittalShare',
    hideNav: true,
  },
};

/** One router child: the pattern the router mounts and what renders there. */
export type RouteMountEntry = {
  path: string;
  mount: Mount;
};

/**
 * The path/mount pairing in registry order. buildAppRouteChildren renders it and
 * the chunk preload matches against it, so the two cannot disagree on what a URL
 * mounts.
 */
export const appRouteMountEntries: readonly RouteMountEntry[] = routeKeys.map(
  (key) => ({ path: paths[key], mount: routeMounts[key] })
);
